import React, { useState, useEffect, useRef } from "react";
import { STEPS } from "../workflow/steps";
import { mastLayouts } from "../config/mastlayouts";
import ProjectDetailsForm from "./ProjectDetailsForm";
import FacadeTable from "./FacadeTable";
import { submitProjectData, submitProjectDataTest, submitSketchData } from "./SubmitProjectData";
import API_BASE from "../config";
import { NORM_DEFAULTS, effectiveTargets } from "../config/normDefaults";
import proj4 from "proj4";
import { getUtmProj } from "../drawing/utils/projection";
import { getMidfieldLine } from "../drawing/utils/geometry";

// Feldmaße aus dem gezeichneten Rechteck messen — in UTM, wie überall im
// Zeichner (in Grad wäre die Ost-West-Achse verzerrt). Liefert die Ecken in
// UTM, die Projektion und die Längen der beiden Kanten in Metern.
const feldMasseAus = (rect) => {
  if (!rect || rect.length < 4) return null;
  const wgs84 = "EPSG:4326";
  const utm = getUtmProj(rect[0]);
  const c = rect.slice(0, 4).map((p) => proj4(wgs84, utm, p));
  const d0 = Math.hypot(c[1][0] - c[0][0], c[1][1] - c[0][1]);
  const d1 = Math.hypot(c[2][0] - c[1][0], c[2][1] - c[1][1]);
  if (!isFinite(d0) || !isFinite(d1) || d0 < 0.01 || d1 < 0.01) return null;
  return { c, utm, d0, d1 };
};

const Sidebar = ({ workflowStep, setWorkflowStep, selectedLayout, setSelectedLayout, setProjectDetails, projectDetails, geometries, setGeometries, mapRef, calculationStatus, setCalculationStatus, refreshProjects, projectId = null, onAfterSubmit = null, navigateToDetail = null, notice = null, layoutOverride = null, setLayoutOverride = () => {} }) => {
  const [layoutDropdownOpen, setLayoutDropdownOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  // Key EN 12193 metrics shown after calculation finishes
  const [calcResults, setCalcResults] = useState(null);
  // Rastervorschau (2026-08-12): nach "Calculation complete" liegen die
  // Rasterpunkte des Laufs als Layer auf der Karte (MapView). Hier nur
  // die Anzeige-Metadaten für den Toggle unter den EN-12193-Werten.
  const [gridPreview, setGridPreview] = useState(null); // { count, minEh, maxEh, visible }
  // Live timer: counts seconds while calculation is running
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef(null);

  // Feldmaße nachschärfen (Philips Wunsch 26.08.2026): nach Schritt 1 stehen
  // Länge × Breite als editierbare Felder unter dem Schritt-Knopf — z. B. um
  // 104,7 auf 105 zu runden. Lokaler Text-State, übernommen bei Enter/Blur.
  const [feldMassEdit, setFeldMassEdit] = useState({ laenge: "", breite: "" });
  useEffect(() => {
    const m = feldMasseAus(geometries?.fieldRectangle);
    if (!m) {
      setFeldMassEdit({ laenge: "", breite: "" });
      return;
    }
    setFeldMassEdit({
      laenge: Math.max(m.d0, m.d1).toFixed(1),
      breite: Math.min(m.d0, m.d1).toFixed(1),
    });
  }, [geometries?.fieldRectangle]);

  // Skaliert das Feldrechteck um seinen Mittelpunkt auf die eingegebenen
  // Maße — Ausrichtung und Lage bleiben, nur die Kantenlängen ändern sich.
  // Gedacht für KLEINE Korrekturen: Masten/Gebäude bleiben stehen, die
  // Mittellinie wird (wie in SketchPage.mittellinieAus) neu abgeleitet, und
  // der [geometries]-Effekt in MapView zeichnet Karte + Mast-Schiene nach.
  const applyFeldMasse = () => {
    const m = feldMasseAus(geometries?.fieldRectangle);
    if (!m) return;
    const L = parseFloat(String(feldMassEdit.laenge).replace(",", "."));
    const B = parseFloat(String(feldMassEdit.breite).replace(",", "."));
    const resetInputs = () =>
      setFeldMassEdit({
        laenge: Math.max(m.d0, m.d1).toFixed(1),
        breite: Math.min(m.d0, m.d1).toFixed(1),
      });
    if (!isFinite(L) || !isFinite(B) || L < 1 || B < 1) {
      resetInputs();
      return;
    }
    // Eingaben der richtigen Achse zuordnen: "Länge" ist die längere Kante,
    // egal in welcher Reihenfolge das Feld gezeichnet wurde.
    const [neuD0, neuD1] = m.d0 >= m.d1 ? [L, B] : [B, L];
    if (Math.abs(neuD0 - m.d0) < 0.005 && Math.abs(neuD1 - m.d1) < 0.005) {
      resetInputs();
      return;
    }
    const { c, utm } = m;
    const cx = (c[0][0] + c[1][0] + c[2][0] + c[3][0]) / 4;
    const cy = (c[0][1] + c[1][1] + c[2][1] + c[3][1]) / 4;
    const u = [(c[1][0] - c[0][0]) / m.d0, (c[1][1] - c[0][1]) / m.d0];
    const v = [(c[2][0] - c[1][0]) / m.d1, (c[2][1] - c[1][1]) / m.d1];
    const p0 = [
      cx - u[0] * neuD0 / 2 - v[0] * neuD1 / 2,
      cy - u[1] * neuD0 / 2 - v[1] * neuD1 / 2,
    ];
    const p1 = [p0[0] + u[0] * neuD0, p0[1] + u[1] * neuD0];
    const p2 = [p1[0] + v[0] * neuD1, p1[1] + v[1] * neuD1];
    const p3 = [p0[0] + v[0] * neuD1, p0[1] + v[1] * neuD1];
    const wgs84 = "EPSG:4326";
    const rectUtm = [p0, p1, p2, p3];
    const rectLL = rectUtm.map((p) => proj4(utm, wgs84, p));
    // Konvention aus dem Zeichner/Backend: das Feldrechteck ist ein
    // GESCHLOSSENER Ring (5 Punkte, letzter = erster) — ohne den fehlt dem
    // Renderer die Schlusskante.
    rectLL.push(rectLL[0]);
    const mid = getMidfieldLine(rectUtm);
    setGeometries((prev) => ({
      ...prev,
      fieldRectangle: rectLL,
      fieldCenterLine: mid ? mid.map((p) => proj4(utm, wgs84, p)) : prev.fieldCenterLine,
    }));
  };

  // „Symmetrieren" (Philips Wunsch 25.08.2026): Haken in Schritt 4, der das
  // gezeichnete Mastrechteck auf die Spielfeld-Mitte zentriert (Relux-
  // Erfahrung: meist wird symmetrisch geplant). Die eigentliche Geometrie-
  // Arbeit macht MapView.symmetrizeMastRect; hier nur der Haken-Zustand.
  const [mastSymmetrisch, setMastSymmetrisch] = useState(false);
  // Sobald neu gezeichnet wird (Schritt < 4), gilt der Haken nicht mehr —
  // er bezieht sich immer auf das AKTUELL gezeichnete Rechteck.
  useEffect(() => {
    if (workflowStep < STEPS.EDIT_MAST_POINTS) setMastSymmetrisch(false);
  }, [workflowStep]);

  // ── Address search state ──────────────────────────────────────────
  const [addressQuery, setAddressQuery] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const debounceRef = useRef(null);

  // Debounced Nominatim geocoding search (300ms delay, free OSM API)
  const searchAddress = (query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 3) { setAddressSuggestions([]); return; }

    debounceRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&countrycodes=de,at,ch&limit=5`;
        const res = await fetch(url, {
          headers: { "User-Agent": "ILS-Sport/1.0 (lighting-calculator)" },
        });
        const data = await res.json();
        setAddressSuggestions(data);
      } catch (e) {
        console.error("Geocoding failed", e);
        setAddressSuggestions([]);
      }
      setAddressLoading(false);
    }, 300);
  };

  // When user selects an address: fly map there, fill project details, advance
  const selectAddress = (item) => {
    const lon = parseFloat(item.lon);
    const lat = parseFloat(item.lat);
    // Pan the map to the selected location.
    // Zoom level 16 (was 18) — keeps surrounding streets visible so users
    // can verify it's the right "Neustadt" before they start drawing.
    // Reported by Rainer 02.04.2026.
    mapRef.current?.flyTo(lon, lat, 16);
    // Auto-fill project address fields from Nominatim response
    const addr = item.address || {};
    setProjectDetails((prev) => ({
      ...prev,
      projectAddress: [addr.road, addr.house_number].filter(Boolean).join(" ") || item.display_name.split(",")[0],
      projectTown: addr.city || addr.town || addr.village || addr.municipality || "",
    }));
    // Clear search state and advance to drawing
    setAddressQuery(item.display_name);
    setAddressSuggestions([]);
    setWorkflowStep(STEPS.DRAW_FIELD_RECTANGLE);
  };
  // Track if calculation is currently running (for cancel button)
  const [isCalculating, setIsCalculating] = useState(false);
  const activeProjectIdRef = useRef(null);

  // Start a 1-second interval timer, returns cleanup function
  const startTimer = () => {
    setElapsedSeconds(0);
    timerRef.current = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);
  };
  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };
  // Clean up timer on unmount
  useEffect(() => () => stopTimer(), []);

  // Format seconds as MM:SS for display
  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const showToast = (message, duration = 3000) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), duration);
  };

  // ── Step-back navigation ──────────────────────────────────────────
  // When stepping back, clear all data from steps AFTER the target step.
  // E.g., going back to step 1 (field) clears mast layout, mast points,
  // buildings, and project details — because they depend on the field.
  const goToStep = (targetStep) => {
    // Only allow stepping back (or to current step for re-editing)
    if (targetStep >= workflowStep) return;

    // Fassaden zuruecksetzen, sobald wir auf oder vor Schritt 5 springen.
    // Muss ueber das Werkzeug laufen: der Zeichner haelt seine eigene
    // `lines`-Liste. Bisher wurde nur der React-State geleert — die alten
    // Linien blieben im Werkzeug, wurden weiter rot gezeichnet und beim
    // naechsten "fertig" wieder angehaengt.
    if (targetStep <= STEPS.DRAW_BUILDINGS) {
      mapRef.current?.clearBuildings?.();
    }

    // Clear geometry/state for all steps after the target
    if (targetStep < STEPS.SELECT_MAST_ARRANGEMENT) {
      // Going back to field drawing — clear everything downstream
      setSelectedLayout(null);
      setGeometries((g) => ({
        ...g,
        mastsRectangle: null,
        mastArrows: [],
        mastPoints: [],
        buildingPolylines: [],
        buildingHeights: [],
      }));
      setCalculationStatus("");
      setCalcResults(null);
      // B-03b (Rainer 02.04.2026): going all the way back to address input
      // also wipes the address-related fields so the user actually re-enters
      // them. Without this, the old address text persists in the input and
      // projectDetails, and "Adresse korrigieren" would silently keep the
      // wrong address.
      if (targetStep === STEPS.INIT) {
        setAddressQuery("");
        setAddressSuggestions([]);
        setProjectDetails((prev) => ({
          ...prev,
          projectAddress: "",
          projectTown: "",
        }));
      }
    } else if (targetStep < STEPS.DRAW_MASTS_RECTANGLE) {
      // Going back to mast layout selection — clear mast geometry & beyond
      setSelectedLayout(null);
      setGeometries((g) => ({
        ...g,
        mastsRectangle: null,
        mastArrows: [],
        mastPoints: [],
        buildingPolylines: [],
        buildingHeights: [],
      }));
      setCalculationStatus("");
      setCalcResults(null);
    } else if (targetStep < STEPS.EDIT_MAST_POINTS) {
      // Going back to mast rectangle — clear mast points & beyond.
      // mastsRectangle ebenfalls: sonst bleibt das ALTE gestrichelte
      // Rechteck waehrend des Neuzeichnens auf der Karte stehen und man
      // sieht zwei Rechtecke uebereinander (Philips Screenshot 25.08.2026).
      setGeometries((g) => ({
        ...g,
        mastsRectangle: null,
        mastArrows: [],
        mastPoints: [],
        buildingPolylines: [],
        buildingHeights: [],
      }));
      setCalculationStatus("");
      setCalcResults(null);
    } else if (targetStep < STEPS.DRAW_BUILDINGS) {
      // Going back to edit masts — clear buildings & beyond
      setGeometries((g) => ({ ...g, buildingPolylines: [], buildingHeights: [] }));
      setCalculationStatus("");
      setCalcResults(null);
    } else if (targetStep === STEPS.DRAW_BUILDINGS) {
      // Re-entering the buildings step itself → clear existing polylines so
      // the user actually starts fresh. Otherwise the old façade stays in
      // state (not visible until the next draw) and gets calculated together
      // with the new one. Reported by Rainer 02.04.2026.
      setGeometries((g) => ({ ...g, buildingPolylines: [], buildingHeights: [] }));
      setCalculationStatus("");
      setCalcResults(null);
    } else if (targetStep < STEPS.SUBMIT_PROJECT) {
      // Going back to project-details only — keep buildings, just clear calc
      setCalculationStatus("");
      setCalcResults(null);
    }

    // Zurückspringen heißt: die Skizze wird geändert — das alte Raster
    // passt dann nicht mehr zur Zeichnung. Komplett vergessen (keepData
    // = false), nicht nur ausblenden.
    mapRef.current?.hideCalculationGrid?.(false);
    setGridPreview(null);

    setWorkflowStep(targetStep);
  };

const handleSubmit = async () => {
   try {
     // Phase 1: Initialisation (create project, geometries, building points)
     // Veraltetes Preset (03.09.): geladene Skizze ohne aktuelles Preset
     // darf nicht abgeschickt werden — das Ersatz-Layout (img: null) wird
     // vom Backend ohnehin mit 422 abgewiesen. Klare Meldung statt
     // "Error during calculation".
     if (layoutOverride) {
       setCalculationStatus("Calculation failed — veraltetes Preset, bitte in Schritt 2 ein aktuelles Layout wählen.");
       showToast("Veraltetes Preset — bitte in Schritt 2 ein aktuelles Layout wählen.");
       return;
     }
     setCalcResults(null);
     // Rastervorschau des vorherigen Laufs raus — während der Berechnung
     // wirft die Kette das alte Raster weg und nullt die eh-Werte, die
     // Anzeige wäre ab jetzt eine Momentaufnahme von gestern.
     mapRef.current?.hideCalculationGrid?.(false);
     setGridPreview(null);
     setCalculationStatus("Initialising...");
     setIsCalculating(true);
     startTimer();
     // layoutOverride: geladene Skizze eines Projekts ohne Preset — dessen
     // gespeicherte angles/symmetry stehen nicht in mastlayouts.js, find()
     // liefe ins Leere und der Submit ginge ohne mastLayout raus (422).
     const mastLayout = layoutOverride
       || mastLayouts.find((layout) => layout.id === selectedLayout);

     // submitProjectData sends to /submit which runs both init + optimisation
     // After ~5-10s the init phase is done and GA starts — we show "Berechnung..."
     // We estimate init takes ~5s, then switch status to calculation phase
     const initTimeout = setTimeout(() => {
       setCalculationStatus("Calculating...");
     }, 5000);

     // New in 2026-05-20: two-phase create. When a projectId prop is set
     // we're INSIDE an existing project (came from CreateProjectPage →
     // SketchPage) — POST to /projects/:id/sketch which skips the
     // create_project step. Otherwise legacy one-shot /submit.
     const result = projectId
       ? await submitSketchData(projectId, { geometries, projectDetails, mastLayout })
       : await submitProjectData({ geometries, projectDetails, mastLayout });

     clearTimeout(initTimeout);
     stopTimer();
     setIsCalculating(false);
     activeProjectIdRef.current = null;
     setCalculationStatus("Calculation complete!");
     await refreshProjects();
     console.log(result);
     const finalProjectId = projectId || result.project_id;
     // Fetch key EN 12193 metrics (Em, Emin/m, RG) to display in sidebar
     if (finalProjectId) {
       try {
         // Signify-Feedback 13.08.: nach "Calculation complete" gehört hier
         // DIESER Lauf hin, nicht die Empfehlung. /results folgt aber bewusst
         // der QW-2-Empfehlung (Sidebar == Variantentabelle bei Wiederbesuch),
         // deshalb zusätzlich /calculations (newest first) holen und die
         // Metriken des jüngsten Laufs ÜBER die /results-Antwort legen.
         // /results bleibt als Basis, damit Felder wie "requirements"
         // (editierbare Norm-Vorgaben) erhalten bleiben.
         const [resResults, resCalcs] = await Promise.all([
           fetch(`${API_BASE}/projects/${finalProjectId}/results`),
           fetch(`${API_BASE}/projects/${finalProjectId}/calculations`),
         ]);
         let merged = resResults.ok ? await resResults.json() : null;
         if (resCalcs.ok) {
           const calcs = await resCalcs.json();
           const latest = calcs[0]; // Endpoint sortiert newest-first
           // metrics == null bei abgebrochenem Lauf ohne Result-Zeile —
           // dann lieber die Empfehlung zeigen als gar nichts.
           if (latest?.metrics) {
             merged = {
               ...(merged || {}),
               pa_ehave: latest.metrics.pa_ehave,
               pa_u: latest.metrics.pa_u,
               rg: latest.metrics.rg,
               isLatestRun: true,
               // Hinweis-Flag: der frische Lauf ist NICHT die Empfehlung —
               // die Variantentabelle zeigt dann einen anderen (besseren) Lauf.
               latestIsRecommended: !!latest.is_recommended,
             };
           }
         }
         if (merged) setCalcResults(merged);
       } catch (e) { console.error("Failed to fetch results", e); }
       // Rastervorschau (2026-08-12): GET /projects/:id liefert die
       // calculationpoints seit jeher als GeoJSON (WGS84) mit eh-Wert —
       // Davids alter Serializer. Direkt nach "complete" gehören die
       // Live-Zeilen genau diesem Lauf, also jetzt auf die Karte damit.
       try {
         const res = await fetch(`${API_BASE}/projects/${finalProjectId}`);
         if (res.ok) {
           const proj = await res.json();
           const pts = (proj.calculationpoints || []).filter(
             (p) => p.geometry && typeof p.eh === "number"
           );
           // Lauter Nullen hieße: die Werte wurden nie geschrieben
           // (Abbruch vor calculate_field) — dann lieber gar nichts
           // zeigen, ein Nullraster sieht aus wie ein Messergebnis.
           if (pts.length && pts.some((p) => p.eh > 0)) {
             const ehs = pts.map((p) => p.eh);
             mapRef.current?.showCalculationGrid?.(pts);
             setGridPreview({
               count: pts.length,
               minEh: Math.min(...ehs),
               maxEh: Math.max(...ehs),
               visible: true,
             });
           }
         }
       } catch (e) { console.error("Failed to fetch grid preview", e); }
     }
     showToast("Calculation successful!");
     // Route-based caller (SketchPage) navigates to the project detail
     // page when the calc finishes so the user sees the comparison
     // table + sweep variants without an extra click.
     if (onAfterSubmit && finalProjectId) {
       onAfterSubmit(finalProjectId);
     }
   } catch (error) {
     stopTimer();
     setIsCalculating(false);
     activeProjectIdRef.current = null;
     setCalculationStatus("Calculation failed");
     console.error(error);
     showToast("Error during calculation.");
   }
 };

const handleSubmitTest = async () => {
  try {
    const mastLayout = mastLayouts.find(layout => layout.id === selectedLayout);
    const result = await submitProjectDataTest({
      geometries,
      projectDetails,
      mastLayout,
    });
    showToast("Data submitted successfully!");
    console.log(result);
  } catch (error) {
    showToast("Error submitting data. Check console for details.");
  }
};

  return (
    <>
    <div
      style={{
        width: "100%",
        height: "100%",
        padding: 12,
        backgroundColor: "#ffffff",
        borderRight: "1px solid #e5e7eb",
        boxShadow: "2px 0 6px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        fontFamily: "system-ui, sans-serif",
        // PRD_followups.md §6/W5: on shorter viewports (laptops, half-screen
        // browser windows) the wizard now overflows once the address pill,
        // sweep checkbox, step-review blocks and EN-12193 result panel all
        // stack up. Let the sidebar scroll internally — the topbar stays
        // pinned, the map keeps its full height, and the user can reach
        // every wizard control with a flick. boxSizing keeps the padding
        // inside the 100% height so the scrollbar doesn't clip.
        overflowY: "auto",
        boxSizing: "border-box",
      }}
    >
      {/* Projektname statt generischem Titel (Philips Wunsch 26.08.2026):
          man sieht sofort, in welchem Projekt man zeichnet. Nummer/Ort als
          Unterzeile wie auf der Detailseite; ohne geladenen Namen (neues
          Projekt, Laden fehlgeschlagen) bleibt der bisherige Titel. */}
      <div>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
          {projectDetails?.projectName || "Create Sports Field"}
        </h3>
        {projectDetails?.projectName && (
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
            {[
              projectDetails.projectNumber ? `Nr. ${projectDetails.projectNumber}` : null,
              projectDetails.projectTown,
            ].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>

      {/* Hinweiszeile, gesetzt von SketchPage bei „Skizze bearbeiten"
          (07.08.2026): sagt, dass die gespeicherte Zeichnung geladen ist
          und der nächste Lauf eine weitere Variante wird — oder warum das
          Laden nicht geklappt hat. */}
      {notice && (
        <div
          style={{
            padding: "8px 10px",
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.4,
            border: `1px solid ${notice.tone === "warn" ? "#fca5a5" : "#bae6fd"}`,
            background: notice.tone === "warn" ? "#fef2f2" : "#f0f9ff",
            color: notice.tone === "warn" ? "#b91c1c" : "#0369a1",
          }}
        >
          {notice.text}
        </div>
      )}

      {/* Undo (Signify-Feedback Dennis 03.07.2026): nimmt den letzten
          Zeichen-Klick zurück — Feld-/Mast-Rechteck-Ecke, Mast-Verschiebung
          oder Fassaden-Punkt. Nur in den Zeichen-Schritten sichtbar; die
          Werkzeuge leben in MapView, daher der Aufruf über mapRef. Ganze
          Schritte korrigiert man weiter über die ✓-Buttons darunter. */}
      {[STEPS.DRAW_FIELD_RECTANGLE, STEPS.DRAW_MASTS_RECTANGLE, STEPS.EDIT_MAST_POINTS, STEPS.DRAW_BUILDINGS].includes(workflowStep) && (
        <button
          onClick={() => mapRef.current?.undo?.()}
          title="Letzten Klick zurücknehmen (Strg+Z)"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            width: "100%",
            padding: "6px 12px",
            background: "#f9fafb",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            color: "#374151",
            cursor: "pointer",
          }}
        >
          ↩ Rückgängig
        </button>
      )}

      {/* Step 0: Address search — shown at INIT, helps user find the sports field */}
      {workflowStep === STEPS.INIT && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, position: "relative" }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
            Enter project address
          </label>
          <input
            type="text"
            value={addressQuery}
            placeholder="e.g. Sports field Berlin..."
            onChange={(e) => {
              setAddressQuery(e.target.value);
              searchAddress(e.target.value);
            }}
            style={{
              padding: "8px 10px", borderRadius: 4, border: "1px solid #d1d5db",
              fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box",
            }}
            onFocus={(e) => e.target.style.borderColor = "#2563eb"}
            onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
          />
          {/* Loading indicator */}
          {addressLoading && (
            <div style={{ fontSize: 11, color: "#9ca3af", padding: "2px 4px" }}>Searching...</div>
          )}
          {/* Autocomplete dropdown */}
          {addressSuggestions.length > 0 && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
              backgroundColor: "#fff", border: "1px solid #d1d5db", borderRadius: 4,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)", maxHeight: 200, overflowY: "auto",
              marginTop: 2,
            }}>
              {addressSuggestions.map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => selectAddress(item)}
                  style={{
                    padding: "8px 10px", fontSize: 12, cursor: "pointer",
                    borderBottom: idx < addressSuggestions.length - 1 ? "1px solid #f3f4f6" : "none",
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = "#eff6ff"}
                  onMouseLeave={(e) => e.target.style.backgroundColor = "#fff"}
                >
                  {item.display_name}
                </div>
              ))}
            </div>
          )}
          {/* Skip button for manual navigation */}
          <button
            onClick={() => setWorkflowStep(STEPS.DRAW_FIELD_RECTANGLE)}
            style={{
              marginTop: 4, background: "none", border: "none", color: "#6b7280",
              fontSize: 11, cursor: "pointer", textDecoration: "underline", padding: 0,
              textAlign: "left",
            }}
          >
            Skip — navigate manually
          </button>
        </div>
      )}

      {/* Step 0 review — shows the selected address with an "Adresse korrigieren"
          button (B-03b, Rainer 02.04.2026 / PRD §2/W1). Without this, once the
          user advances past STEPS.INIT they have no way back to the address
          field except F5. The button calls goToStep(STEPS.INIT), which now also
          clears address state (see goToStep above). */}
      {workflowStep > STEPS.INIT && projectDetails.projectAddress && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "6px 8px",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 4,
            fontSize: 12,
            color: "#374151",
          }}
        >
          <span
            title={`${projectDetails.projectAddress}${projectDetails.projectTown ? ", " + projectDetails.projectTown : ""}`}
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            📍 {projectDetails.projectAddress}
          </span>
          <button
            onClick={() => goToStep(STEPS.INIT)}
            title="Adresse zurücksetzen und neu eingeben"
            style={{
              background: "none",
              border: "1px solid #d1d5db",
              borderRadius: 4,
              color: "#6b7280",
              fontSize: 11,
              cursor: "pointer",
              padding: "2px 8px",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            Adresse korrigieren
          </button>
        </div>
      )}

      {/* Step 1: Draw Field Rectangle — click to go back and redraw */}
      {workflowStep >= STEPS.DRAW_FIELD_RECTANGLE && (
        <button
          style={{
            ...buttonStyle(
              workflowStep === STEPS.INIT || workflowStep === STEPS.DRAW_FIELD_RECTANGLE,
              false  /* never fully disabled — always clickable to go back */
            ),
            width: "100%",
            // Completed steps get a subtle "clickable" look
            ...(workflowStep > STEPS.DRAW_FIELD_RECTANGLE ? { opacity: 0.7, cursor: "pointer", pointerEvents: "auto" } : {}),
          }}
          onClick={() => {
            if (workflowStep > STEPS.DRAW_FIELD_RECTANGLE) {
              goToStep(STEPS.DRAW_FIELD_RECTANGLE);
            } else {
              setWorkflowStep(STEPS.DRAW_FIELD_RECTANGLE);
            }
          }}
        >
          1. Draw Field Rectangle {workflowStep > STEPS.DRAW_FIELD_RECTANGLE ? "✓" : ""}
        </button>
      )}

      {/* Editierbare Feldmaße (Philips Wunsch 26.08.2026): kleine Korrekturen
          ohne Neuzeichnen — Skalierung um den Feldmittelpunkt, Enter/Blur
          übernimmt. */}
      {workflowStep > STEPS.DRAW_FIELD_RECTANGLE && feldMassEdit.laenge !== "" && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#374151", padding: "0 2px" }}>
          <span style={{ color: "#6b7280" }}>Feldmaß:</span>
          {["laenge", "breite"].map((key, i) => (
            <React.Fragment key={key}>
              {i === 1 && <span>×</span>}
              <input
                type="text"
                inputMode="decimal"
                value={feldMassEdit[key]}
                title={key === "laenge" ? "Feldlänge (m) — Enter übernimmt" : "Feldbreite (m) — Enter übernimmt"}
                onChange={(e) => setFeldMassEdit((prev) => ({ ...prev, [key]: e.target.value }))}
                onBlur={applyFeldMasse}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                style={{
                  width: 52,
                  padding: "3px 5px",
                  fontSize: 12,
                  textAlign: "right",
                  border: "1px solid #d1d5db",
                  borderRadius: 4,
                }}
              />
            </React.Fragment>
          ))}
          <span>m</span>
        </div>
      )}

      {/* Step 2: Select Mast Layout — click to go back and pick a different layout */}
      {workflowStep >= STEPS.SELECT_MAST_ARRANGEMENT && (
        <div>
          <button
            style={{
              ...buttonStyle(workflowStep === STEPS.SELECT_MAST_ARRANGEMENT, false),
              width: "100%",
              ...(workflowStep > STEPS.SELECT_MAST_ARRANGEMENT ? { opacity: 0.7, cursor: "pointer", pointerEvents: "auto" } : {}),
            }}
            onClick={() => {
              if (workflowStep > STEPS.SELECT_MAST_ARRANGEMENT) {
                goToStep(STEPS.SELECT_MAST_ARRANGEMENT);
                setLayoutDropdownOpen(true);
              } else {
                setLayoutDropdownOpen((o) => !o);
              }
            }}
          >
            2. Select Mast Layout {workflowStep > STEPS.SELECT_MAST_ARRANGEMENT ? "✓" : ""}
          </button>
      
          <div
            style={{
              marginTop: 5,
              display: "flex",
              flexDirection: "column",
              gap: 8,
              maxHeight: 300,
              overflowY: "auto",
            }}
          >
            {/* Projekt ohne Preset (geladene Skizze): es gibt kein Bild und
                keinen Katalogeintrag — stattdessen kurz benennen, was
                übernommen wurde. Bei geöffnetem Dropdown stehen darunter
                trotzdem alle Presets zur Wahl (Bug 26.08.: die Liste war
                bei Override hart leer, ein Preset-Wechsel damit unmöglich). */}
            {layoutOverride && (
              <div style={{ fontSize: 12, color: "#374151", padding: "4px 2px" }}>
                {layoutOverride.name}
                {/* Veraltetes Preset (03.09.): rot, weil damit nicht mehr
                    gerechnet werden kann — Submit ist gesperrt, bis ein
                    aktuelles Preset gewählt wurde. */}
                <div style={{ fontSize: 11, color: "#dc2626", fontWeight: 600 }}>
                  Veraltetes Preset — entspricht keinem aktuellen Standard-Layout.
                </div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>
                  {layoutDropdownOpen
                    ? "Ein Preset unten ersetzt es; danach das Mastrechteck neu ziehen."
                    : "Bitte hier ein aktuelles Layout wählen, danach das Mastrechteck neu ziehen."}
                </div>
              </div>
            )}
            {(layoutOverride
              ? (layoutDropdownOpen ? mastLayouts : [])
              : selectedLayout
              ? mastLayouts.filter((layout) => layout.id === selectedLayout)
              : layoutDropdownOpen
              ? mastLayouts
              : []
            ).map((layout) => (
              <div
                key={layout.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: 6,
                  borderRadius: 4,
                  border: layoutDropdownOpen && selectedLayout !== layout.id? "1px solid #d1d5db": "none",
                  backgroundColor: layoutDropdownOpen && selectedLayout !== layout.id? "#f9fafb": "transparent",
                  cursor:layoutDropdownOpen && selectedLayout !== layout.id? "pointer": "default",
                }}
                onClick={() => {
                  setSelectedLayout(layout.id);
                  // Override raus, sonst gewinnt es beim Submit weiter
                  // Vorrang (mastLayout = layoutOverride || find(...)).
                  setLayoutOverride(null);
                  setLayoutDropdownOpen(false);
                  // Don't advance yet — user needs to enter mast height first
                }}
              >
                <img
                  src={layout.img}
                  alt={layout.name}
                  style={{ maxWidth: 80, maxHeight: 80, objectFit: "contain" }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{layout.name}</span>
                  <span style={{ fontSize: 12, color: "#4b5563", whiteSpace: "pre-wrap" }}>
                    {layout.description}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Mast height input — shown after layout is selected, before drawing masts */}
          {selectedLayout && workflowStep === STEPS.SELECT_MAST_ARRANGEMENT && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: "#374151" }}>
                Mast Height (m):
                <input
                  type="number"
                  name="mastHeight"
                  value={projectDetails.mastHeight}
                  onChange={(e) => setProjectDetails((prev) => ({ ...prev, mastHeight: e.target.value }))}
                  placeholder="e.g. 18"
                  style={{
                    width: "100%", padding: "6px 8px", borderRadius: 4,
                    border: "1px solid #d1d5db", fontSize: 13, marginTop: 2,
                  }}
                />
              </label>
              <button
                style={{
                  ...buttonStyle(true, false),
                  width: "100%",
                  // Grey out if no mast height entered
                  ...(!projectDetails.mastHeight ? { opacity: 0.4, cursor: "not-allowed" } : {}),
                }}
                onClick={() => {
                  if (!projectDetails.mastHeight) {
                    showToast("Please enter the mast height.");
                    return;
                  }
                  setWorkflowStep(STEPS.DRAW_MASTS_RECTANGLE);
                }}
              >
                Confirm
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Draw Mast Rectangle — click to go back and reposition masts */}
      {workflowStep >= STEPS.DRAW_MASTS_RECTANGLE && selectedLayout && (
        <button
          style={{
            ...buttonStyle(workflowStep === STEPS.DRAW_MASTS_RECTANGLE, false),
            width: "100%",
            ...(workflowStep > STEPS.DRAW_MASTS_RECTANGLE ? { opacity: 0.7, cursor: "pointer", pointerEvents: "auto" } : {}),
          }}
          onClick={() => {
            if (workflowStep > STEPS.DRAW_MASTS_RECTANGLE) {
              goToStep(STEPS.DRAW_MASTS_RECTANGLE);
            } else {
              setWorkflowStep(STEPS.DRAW_MASTS_RECTANGLE);
            }
          }}
        >
          3. Draw Mast Layout Rectangle {workflowStep > STEPS.DRAW_MASTS_RECTANGLE ? "✓" : ""}
        </button>
      )}

      {/* Step 4: Edit Masts */}
      {workflowStep === STEPS.EDIT_MAST_POINTS && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <button style={{...buttonStyle(workflowStep === STEPS.EDIT_MAST_POINTS, workflowStep > STEPS.EDIT_MAST_POINTS), width: "100%",}}
            >4. Edit Masts</button>
          {/* Symmetrieren (Philips Wunsch 25.08.2026): zentriert das
              Mastrechteck auf die Feldmitte — Masten stehen dann
              punktsymmetrisch zum Mittelpunkt, wie in den meisten
              Relux-Planungen. Setzt haendisch verschobene Masten
              zurueck, deshalb sitzt der Haken VOR dem Editieren. */}
          <label style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 12, color: "#374151", padding: "2px 2px",
            cursor: "pointer", userSelect: "none",
          }}>
            <input
              type="checkbox"
              checked={mastSymmetrisch}
              onChange={(e) => {
                const soll = e.target.checked;
                const layoutObj = layoutOverride
                  || mastLayouts.find((l) => l.id === selectedLayout);
                const ok = mapRef.current?.symmetrizeMastRect?.(soll, layoutObj);
                if (ok) setMastSymmetrisch(soll);
                else showToast("Symmetrieren nicht möglich — Feld oder Layout fehlt.");
              }}
            />
            Symmetrieren — Masten symmetrisch zur Feldmitte
          </label>
          {mastSymmetrisch && (
            <p style={{ fontSize: 11, color: "#6b7280", margin: "0 0 2px" }}>
              Mastrechteck auf die Feldmitte zentriert. Abhaken stellt die
              gezeichnete Lage wieder her; beides setzt verschobene Masten zurück.
            </p>
          )}
          <button style={{...buttonStyle(false, workflowStep > STEPS.EDIT_MAST_POINTS), width: "100%",}}
            onClick={() => {setWorkflowStep(STEPS.DRAW_BUILDINGS);}}
          >Finish</button>
        </div>
      )}

      {/* Step 4 completed — click to go back and re-edit masts */}
      {workflowStep > STEPS.EDIT_MAST_POINTS && (
        <button
          style={{
            ...buttonStyle(false, false),
            width: "100%",
            opacity: 0.7, cursor: "pointer", pointerEvents: "auto",
          }}
          onClick={() => goToStep(STEPS.EDIT_MAST_POINTS)}
        >
          4. Edit Masts ✓
        </button>
      )}

      {/* Step 5: Draw Buildings — überspringbar, wenn keine Anwohner in der
          Nähe sind (Signify-Feedback Dennis 03.07.2026). Die Engine kommt
          mit 0 Gebäuden klar: leere Fassaden-Arrays ⇒ ks/ev = 0 = bestanden
          (Guard in conversion_june.calculate_results_c). */}
      {workflowStep === STEPS.DRAW_BUILDINGS && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <button style={{...buttonStyle(workflowStep === STEPS.DRAW_BUILDINGS, false), width: "100%",}}
            >5. Draw Buildings</button>
          {/* Hinweis, solange keine Fassade gezeichnet ist */}
          {(!geometries.buildingPolylines || geometries.buildingPolylines.length === 0) && (
            <p style={{ fontSize: 11, color: "#6b7280", margin: "2px 0" }}>
              Anwohner-Fassaden einzeichnen (für die Streulicht-/Blendungs-Bewertung).
              Keine Anwohner in der Nähe? Dann Schritt überspringen.
            </p>
          )}
          {/* Uebersicht der gezeichneten Fassaden: Nummer (= Label auf der
              Karte), Hoehenlage relativ zum Platz, Gebaeudehoehe, Laenge.
              Ergaenzt den Live-Kasten des Zeichenwerkzeugs, der immer nur
              die gerade gezeichnete Fassade zeigt. */}
          <FacadeTable
            geometries={geometries}
            setGeometries={setGeometries}
            onDelete={(index) => mapRef.current?.deleteBuilding?.(index)}
          />
          <button style={{
              ...buttonStyle(false, false), width: "100%",
              // Grey out Finish button if no buildings drawn
              ...((!geometries.buildingPolylines || geometries.buildingPolylines.length === 0)
                ? { opacity: 0.4, cursor: "not-allowed" } : {}),
            }}
            onClick={() => {
              if (!geometries.buildingPolylines || geometries.buildingPolylines.length === 0) {
                showToast("Keine Fassade gezeichnet — Schritt ggf. überspringen.");
                return;
              }
              setWorkflowStep(STEPS.ENTER_PROJECT_DETAILS);
            }}
          >Finish</button>
          {/* Überspringen: nur anbieten, solange nichts gezeichnet wurde */}
          {(!geometries.buildingPolylines || geometries.buildingPolylines.length === 0) && (
            <button
              style={{
                ...buttonStyle(false, false), width: "100%",
                fontSize: 12, color: "#6b7280",
              }}
              onClick={() => setWorkflowStep(STEPS.ENTER_PROJECT_DETAILS)}
            >
              Überspringen — keine Anwohner
            </button>
          )}
        </div>
      )}
      {/* Step 5 completed — click to go back and re-draw buildings */}
      {workflowStep > STEPS.DRAW_BUILDINGS && (
        <button
          style={{
            ...buttonStyle(false, false),
            width: "100%",
            opacity: 0.7, cursor: "pointer", pointerEvents: "auto",
          }}
          onClick={() => goToStep(STEPS.DRAW_BUILDINGS)}
        >
          5. Draw Buildings {(geometries.buildingPolylines?.length ?? 0) > 0 ? "✓" : "(übersprungen)"}
        </button>
      )}

      {/* Step 6: Enter Project Details */}
      {workflowStep === STEPS.ENTER_PROJECT_DETAILS && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: 4 }}>

          <button
            style={{
              ...buttonStyle(workflowStep === STEPS.ENTER_PROJECT_DETAILS, workflowStep > STEPS.ENTER_PROJECT_DETAILS),
              width: "100%",
            }}
          >
            {projectId ? "6. Berechnungs-Einstellungen" : "6. Enter Project Details"}
          </button>

          <ProjectDetailsForm
            projectDetails={projectDetails}
            setProjectDetails={setProjectDetails}
            hideProjectFields={Boolean(projectId)}
          />

          <button
            style={{
              ...buttonStyle(false, workflowStep > STEPS.ENTER_PROJECT_DETAILS),
              width: "100%",
            }}
            onClick={() => {
              setWorkflowStep(STEPS.SUBMIT_PROJECT);
              console.log("Project Details Saved:", projectDetails);
            }}
          >
            Finish
          </button>
        </div>
      )}

      {/* Step 6 completed — click to go back and edit details.
          In sketch-flow (projectId set), name/number/address came from
          CreateProjectPage; we only show the calc-time settings
          headline so the user can re-tick the sweep checkbox. */}
      {workflowStep > STEPS.ENTER_PROJECT_DETAILS && (
        <div
          style={{ display: "flex", flexDirection: "column", gap: 6, padding: "8px 0", cursor: "pointer", opacity: 0.7 }}
          onClick={() => goToStep(STEPS.ENTER_PROJECT_DETAILS)}
          title="Click to edit project details"
        >
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            6. {projectId ? "Berechnungs-Einstellungen" : "Project Details"} ✓
          </div>
          {!projectId && (
            <>
              <div><strong>Project Name:</strong> {projectDetails.projectName}</div>
              <div><strong>Project Number:</strong> {projectDetails.projectNumber}</div>
              <div><strong>Project Address:</strong> {projectDetails.projectAddress}</div>
              <div><strong>Project Town:</strong> {projectDetails.projectTown}</div>
            </>
          )}
          {projectId && (
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              {/* QW-3: gewählte Lichtfarbe in der ✓-Zusammenfassung
                  anzeigen, damit der Filter vor dem Submit sichtbar ist */}
              <div>
                Lichtfarbe:&nbsp;{projectDetails.cct ? `${projectDetails.cct} K` : "Alle"}
              </div>
              {/* Norm-Vorgaben (2026-08-13) in der ✓-Zusammenfassung, damit
                  eine Abweichung von der Norm vor dem Submit sichtbar ist. */}
              <div>
                Norm:&nbsp;
                {(() => {
                  const t = effectiveTargets(projectDetails);
                  const edited =
                    projectDetails.emTarget != null ||
                    projectDetails.uoTarget != null ||
                    projectDetails.rgMax != null;
                  return `Em ≥ ${t.em} lx · Emin/Em ≥ ${String(t.uo).replace(".", ",")} · RG ≤ ${t.rg}${edited ? " (angepasst)" : ""}`;
                })()}
              </div>
              <div>
                Sweep:&nbsp;{projectDetails.autoLuminaireSweep === false ? "deaktiviert" : "aktiviert"}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 7: Submit Project */}
      {workflowStep === STEPS.SUBMIT_PROJECT && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <button style={{...buttonStyle(workflowStep === STEPS.SUBMIT_PROJECT, workflowStep > STEPS.SUBMIT_PROJECT), width: "100%",
              // Veraltetes Preset: Knopf ausgegraut, Grund steht darunter.
              ...(layoutOverride ? { opacity: 0.5, cursor: "not-allowed" } : {}),
            }}
            disabled={Boolean(layoutOverride)}
            title={layoutOverride ? "Veraltetes Preset — bitte in Schritt 2 ein aktuelles Layout wählen." : undefined}
            onClick={async () => {
              await handleSubmit();
            }}
            >7. Submit Project</button>
            {layoutOverride && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#dc2626", textAlign: "center" }}>
                Veraltetes Preset — bitte in Schritt 2 ein aktuelles Layout wählen.
              </div>
            )}
            {calculationStatus && (
              <div
                style={{
                  marginTop: 8,
                  fontWeight: "bold",
                  textAlign: "center",
                  color: calculationStatus.includes("failed") ? "#dc2626"
                       : calculationStatus.includes("complete") ? "#15803d"
                       : "#2563eb",
                }}
              >
                {calculationStatus}
                {/* Show live timer while calculation is running, final time when done */}
                <div style={{ fontSize: 22, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                  {formatTime(elapsedSeconds)}
                </div>
                {/* ── Hinweis bei langen Laeufen (Philip 05.08.) ──────────────
                    Der Nutzer sieht sonst minutenlang nur "Calculating..." und
                    eine hochzaehlende Uhr und weiss nicht, ob noch etwas
                    passiert. Ausgeloest wird ueber die VERSTRICHENE ZEIT, nicht
                    ueber die GA-Stufe: /submit bzw. /projects/:id/sketch sind
                    blockierende Aufrufe, die erst am Ende antworten — die
                    Oberflaeche erfaehrt waehrenddessen nichts vom Fortschritt
                    (es gibt keinen Progress-Endpunkt, nur /cancel-latest).
                    75 s als Schwelle kommt aus der 30-Projekte-Messung vom
                    05.08.: Median 51 s, loesbare Projekte liegen meist darunter.
                    Wer laenger braucht, ist in Stufe 2 der Eskalation — genau
                    der Fall, den die Meldung beschreiben soll. Ab 4 Minuten
                    wird deutlicher, dass es dauern kann und dass es unten einen
                    Abbrechen-Knopf gibt. */}
                {isCalculating && elapsedSeconds >= 75 && (
                  <div style={{
                    marginTop: 8, fontSize: 12, fontWeight: 400, lineHeight: 1.45,
                    color: "#475569", textAlign: "left",
                    padding: "6px 8px", borderRadius: 4,
                    backgroundColor: "#f8fafc", border: "1px solid #e2e8f0",
                  }}>
                    {elapsedSeconds < 240
                      ? "Das ist ein komplexeres Projekt. Es kann noch etwas dauern."
                      : "Der Optimierer sucht weiter. Bei schwierigen Geometrien dauert das einige Minuten — Sie können den Lauf unten abbrechen, das bisher beste Ergebnis bleibt erhalten."}
                  </div>
                )}
                {/* Cancel button — stops GA after current generation, saves best result */}
                {isCalculating && (
                  <button
                    style={{
                      marginTop: 8, padding: "6px 16px", borderRadius: 4,
                      border: "1px solid #dc2626", backgroundColor: "#fef2f2",
                      color: "#dc2626", fontWeight: 600, fontSize: 13,
                      cursor: "pointer", width: "100%",
                    }}
                    onClick={async () => {
                      try {
                        await fetch(`${API_BASE}/cancel-latest`, { method: "POST" });
                        setCalculationStatus("Cancel requested...");
                        showToast("Calculation will stop after current generation.");
                      } catch (e) {
                        console.error("Cancel failed", e);
                      }
                    }}
                  >
                    ✕ Cancel calculation
                  </button>
                )}
              </div>
            )}
            {/* Key EN 12193 metrics — shown after calculation finishes */}
            {calcResults && (
              <div style={{
                marginTop: 10, padding: 10, borderRadius: 6,
                backgroundColor: "#f0f9ff", border: "1px solid #bae6fd",
                fontSize: 13, lineHeight: 1.8,
              }}>
                <div style={{ fontWeight: 600, marginBottom: 4, color: "#0369a1" }}>
                  {/* Ehrliche Beschriftung (13.08.): direkt nach dem Lauf stehen
                      hier dessen Werte, beim Wiederbesuch die der Empfehlung. */}
                  {calcResults.isLatestRun ? "Ergebnis dieses Laufs (EN 12193)" : "Results (EN 12193)"}
                </div>
                {calcResults.isLatestRun && calcResults.latestIsRecommended === false && (
                  <div style={{ color: "#92400e", fontSize: 11, marginBottom: 4 }}>
                    ★ Die Empfehlung ist eine andere Variante — siehe Variantentabelle.
                  </div>
                )}
                {/* Nachweis auf den ANZEIGEWERTEN, mit ≥/≤ statt >/< (2026-08-11,
                    Philips Fund im Report): EN 12193 nennt Mindest-/Höchstwerte —
                    exakt 0,50 bzw. RG 55,0 erfüllt die Norm. Vorher wurde der
                    Rohwert strikt verglichen: 0,496 stand als "0,50" da, war aber
                    rot — für den Leser ein Widerspruch. Deshalb erst auf die
                    angezeigte Genauigkeit runden, DANN prüfen; gleiche Logik wie
                    _calc_passes im Backend und dataFallbacks.ts im Report. */}
                {/* 2026-08-13: req-Werte kommen vom Backend mit (editierbare
                    Norm-Vorgaben je Projekt); Fallback = Norm-Defaults für
                    ältere Backend-Stände ohne "requirements" im Response. */}
                {/* Average maintained illuminance on playing area */}
                <div style={{ color: Math.round(calcResults.pa_ehave) >= (calcResults.requirements?.em ?? NORM_DEFAULTS.em) ? "#15803d" : "#dc2626" }}>
                  E<sub>m</sub>: {Math.round(calcResults.pa_ehave)} lux
                  <span style={{ color: "#6b7280", fontSize: 11 }}> (req: ≥ {calcResults.requirements?.em ?? NORM_DEFAULTS.em})</span>
                </div>
                {/* Uniformity on playing area (Emin / Eavg) */}
                <div style={{ color: Number(calcResults.pa_u.toFixed(2)) >= (calcResults.requirements?.uo ?? NORM_DEFAULTS.uo) ? "#15803d" : "#dc2626" }}>
                  E<sub>min/m</sub>: {calcResults.pa_u.toFixed(2).replace(".", ",")}
                  <span style={{ color: "#6b7280", fontSize: 11 }}> (req: ≥ {(calcResults.requirements?.uo ?? NORM_DEFAULTS.uo).toFixed(2).replace(".", ",")})</span>
                </div>
                {/* Glare rating (threshold increment) */}
                <div style={{ color: Number(calcResults.rg.toFixed(1)) <= (calcResults.requirements?.rg ?? NORM_DEFAULTS.rg) ? "#15803d" : "#dc2626" }}>
                  R<sub>G</sub>: {calcResults.rg.toFixed(1).replace(".", ",")}
                  <span style={{ color: "#6b7280", fontSize: 11 }}> (req: ≤ {calcResults.requirements?.rg ?? NORM_DEFAULTS.rg})</span>
                </div>
                {/* Rastervorschau ein-/ausblenden. Die Punkte bleiben beim
                    Ausblenden am Map-Objekt gecacht (keepData), Einblenden
                    braucht darum keinen neuen Fetch. Min–Max zeigt die
                    Spreizung, die hinter der Farbskala blau→gelb→rot steckt;
                    der Wert je Punkt erscheint beim Überfahren mit der Maus. */}
                {gridPreview && (
                  <label style={{
                    display: "flex", alignItems: "center", gap: 6,
                    marginTop: 8, cursor: "pointer", color: "#0369a1",
                    fontSize: 12, userSelect: "none",
                  }}>
                    <input
                      type="checkbox"
                      checked={gridPreview.visible}
                      onChange={(e) => {
                        const visible = e.target.checked;
                        if (visible) mapRef.current?.showCalculationGrid?.();
                        else mapRef.current?.hideCalculationGrid?.();
                        setGridPreview((g) => ({ ...g, visible }));
                      }}
                      style={{ accentColor: "#0369a1" }}
                    />
                    Raster auf Karte ({Math.round(gridPreview.minEh)}–{Math.round(gridPreview.maxEh)} lx, {gridPreview.count} Punkte)
                  </label>
                )}
                {/* Manual navigation to the project-detail page so the user
                    sees the comparison table with all sweep variants. We
                    used to auto-navigate the moment the calc finished, but
                    that yanked the user away before they could read the
                    EN-12193 numbers on this panel. Now they click when ready. */}
                {navigateToDetail && projectId && (
                  <button
                    type="button"
                    onClick={() => navigateToDetail(projectId)}
                    style={{
                      marginTop: 10, width: "100%",
                      padding: "8px 12px", borderRadius: 6,
                      background: "var(--signify-green, #00e487)", color: "#0a2326",
                      border: "none", fontWeight: 700, fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    → Alle Varianten ansehen
                  </button>
                )}
              </div>
            )}
        </div>
      )}
      {/*workflowStep === STEPS.SUBMIT_PROJECT && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <button style={{...buttonStyle(workflowStep === STEPS.SUBMIT_PROJECT, workflowStep > STEPS.SUBMIT_PROJECT), width: "100%",}}
            onClick={async () => {
              await handleSubmitTest();
            }}
            >7. Submit Project Test</button>
        </div>
      )*/}
    </div>
    {toastMessage && (
        <div
          style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "#111827",
          color: "#fff",
          padding: "20px 30px",
          borderRadius: 8,
          boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
          fontSize: 18,
          zIndex: 9999,
          pointerEvents: "none",
          opacity: 1,
          transition: "opacity 0.3s ease",
          textAlign: "center",
          maxWidth: "80%",
        }}
        >
          {toastMessage}
        </div>
      )}
    </>
  );
};

const buttonStyle = (active, disabled = false) => ({
  padding: "10px 12px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  backgroundColor: active ? "#2563eb" : "#f9fafb",
  color: active ? "#ffffff" : "#111827",
  cursor: disabled ? "not-allowed" : "pointer",
  textAlign: "left",
  fontSize: 14,
  fontWeight: 500,
  transition: "all 0.15s ease",
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? "none" : "auto",
});

export default Sidebar;